import React, { useState, useEffect } from 'react';
import { Plus, Edit, Trash2, Play, Pause, MoreVertical, Search, Copy, Share, Archive } from 'lucide-react';
import { botService } from '../../services/botService';
import { useBotBuilder } from './BotManagement';
import toast from 'react-hot-toast';
import apiClient from '../../services/authService';

const MyList = () => {
  const { setBotBuilderMode, setSelectedBot } = useBotBuilder();
  const [bots, setBots] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showDuplicateModal, setShowDuplicateModal] = useState(false);
  const [newBotName, setNewBotName] = useState('');
  const [duplicateBotName, setDuplicateBotName] = useState('');
  const [selectedBotForDuplicate, setSelectedBotForDuplicate] = useState(null);
  const [creating, setCreating] = useState(false);
  const [duplicating, setDuplicating] = useState(false);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [totalElements, setTotalElements] = useState(0);
  const [sortBy, setSortBy] = useState('createdAt');
  const [sortDir, setSortDir] = useState('desc');
  const [openMenuId, setOpenMenuId] = useState(null);
  const [showUnpublishConfirm, setShowUnpublishConfirm] = useState(false);
  const [unpublishBotId, setUnpublishBotId] = useState(null);
  const [activeSessionCount, setActiveSessionCount] = useState(0);

  useEffect(() => {
    loadMyBots();
  }, [currentPage, sortBy, sortDir]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (openMenuId && !event.target.closest('.relative')) {
        setOpenMenuId(null);
      }
    };

    if (openMenuId) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [openMenuId]);

  const loadMyBots = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await botService.getMyListBots(currentPage, 12, sortBy, sortDir);
      setBots(response.content || []);
      setTotalPages(response.totalPages || 0);
      setTotalElements(response.totalElements || 0);
    } catch (error) {
      console.error('Error loading my bots:', error);
      setError('Failed to load your bots. Please try again.');
      setBots([]);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateBot = async () => {
    if (!newBotName.trim()) {
      console.log('Bot name is empty, not creating bot');
      return;
    }

    console.log('Creating bot with name:', newBotName);
    
    try {
      setCreating(true);
      setError(null);
      
      const botData = {
        name: newBotName,
        description: '',
        status: '10001'
      };
      
      console.log('Calling botService.createBot with data:', botData);
      const newBot = await botService.createBot(botData);
      console.log('Bot created successfully:', newBot);
      
      setSelectedBot(newBot);
      setBotBuilderMode('create');
      setShowCreateModal(false);
      setNewBotName('');
      loadMyBots();
    } catch (error) {
      console.error('Error creating bot:', error);
      setError(`Failed to create bot: ${error.message}`);
    } finally {
      setCreating(false);
    }
  };

  const handleEditBot = (bot) => {
    setSelectedBot(bot);
    setBotBuilderMode('edit');
  };

  const handleDeleteBot = async (botId) => {
    if (window.confirm('Are you sure you want to delete this bot?')) {
      try {
        await botService.deleteBot(botId);
        loadMyBots();
      } catch (error) {
        console.error('Error deleting bot:', error);
        setError('Failed to delete bot. Please try again.');
      }
    }
  };

  const handlePublishBot = async (botId) => {
    try {
      await botService.publishBot(botId);
      toast.success('Bot published successfully');
      setOpenMenuId(null); // Close menu after action
      loadMyBots();
    } catch (error) {
      console.error('Error publishing bot:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Failed to publish bot. Please try again.';
      setError(errorMessage);
      toast.error(errorMessage);
    }
  };

  const handleUnpublishBot = async (botId) => {
    try {
      // Check for active sessions before unpublishing
      const sessionCount = await botService.getActiveSessionCount(botId);
      
      if (sessionCount > 0) {
        // Show confirmation dialog
        setUnpublishBotId(botId);
        setActiveSessionCount(sessionCount);
        setShowUnpublishConfirm(true);
        setOpenMenuId(null); // Close menu
        return;
      }
      
      // No active sessions, proceed with unpublish
      await performUnpublish(botId);
    } catch (error) {
      console.error('Error checking active sessions or unpublishing bot:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Failed to unpublish bot. Please try again.';
      setError(errorMessage);
      toast.error(errorMessage);
    }
  };

  const performUnpublish = async (botId) => {
    try {
      await botService.unpublishBot(botId);
      toast.success('Bot unpublished successfully');
      setOpenMenuId(null); // Close menu after action
      loadMyBots();
    } catch (error) {
      console.error('Error unpublishing bot:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Failed to unpublish bot. Please try again.';
      setError(errorMessage);
      toast.error(errorMessage);
    }
  };

  const handleConfirmUnpublish = async () => {
    if (unpublishBotId) {
      setShowUnpublishConfirm(false);
      await performUnpublish(unpublishBotId);
      setUnpublishBotId(null);
      setActiveSessionCount(0);
    }
  };

  const handleCancelUnpublish = () => {
    setShowUnpublishConfirm(false);
    setUnpublishBotId(null);
    setActiveSessionCount(0);
  };

  const handleArchiveBot = async (botId) => {
    try {
      const requestData = {
        botStatus: '10003'
      };
      const response = await apiClient.patch(`/bots/${botId}`, requestData);
      toast.success('Bot archived successfully');
      setOpenMenuId(null); // Close menu after action
      loadMyBots();
    } catch (error) {
      console.error('Error archiving bot:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Failed to archive bot. Please try again.';
      setError(errorMessage);
      toast.error(errorMessage);
    }
  };

  const handleRestoreToDraft = async (botId) => {
    try {
      await botService.unpublishBot(botId); // Same logic as unpublish (sets to DRAFT)
      toast.success('Bot restored to draft successfully');
      setOpenMenuId(null); // Close menu after action
      loadMyBots();
    } catch (error) {
      console.error('Error restoring bot to draft:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Failed to restore bot to draft. Please try again.';
      setError(errorMessage);
      toast.error(errorMessage);
    }
  };

  const handleStatusChange = (botId, action) => {
    setOpenMenuId(null); // Close menu
    if (action === 'publish') {
      handlePublishBot(botId);
    } else if (action === 'unpublish') {
      handleUnpublishBot(botId);
    } else if (action === 'archive') {
      handleArchiveBot(botId);
    } else if (action === 'restore') {
      handleRestoreToDraft(botId);
    }
  };

  const handleDuplicateBot = (bot) => {
    setSelectedBotForDuplicate(bot);
    setDuplicateBotName(`${bot.botName} (Copy)`);
    setShowDuplicateModal(true);
  };

  const handleConfirmDuplicate = async () => {
    if (!duplicateBotName.trim() || !selectedBotForDuplicate) {
      return;
    }

    try {
      setDuplicating(true);
      setError(null);
      await botService.duplicateBot(selectedBotForDuplicate.botId, duplicateBotName.trim());
      setShowDuplicateModal(false);
      setDuplicateBotName('');
      setSelectedBotForDuplicate(null);
      loadMyBots();
    } catch (error) {
      console.error('Error duplicating bot:', error);
      setError('Failed to duplicate bot. Please try again.');
    } finally {
      setDuplicating(false);
    }
  };

  const handleSort = (field) => {
    if (sortBy === field) {
      setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortDir('desc');
    }
    setCurrentPage(0);
  };

  const filteredBots = bots.filter(bot =>
    bot.botName && bot.botName.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getStatusColor = (status) => {
    const normalizedStatus = status === '10001' ? 'DRAFT' : 
                            status === '10002' ? 'PUBLISHED' : 
                            status === '10003' ? 'ARCHIVED' : status;
    
    switch (normalizedStatus) {
      case 'PUBLISHED':
        return 'bg-green-100 text-green-800';
      case 'DRAFT':
        return 'bg-yellow-100 text-yellow-800';
      case 'ARCHIVED':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };


  return (
    <div className="h-full flex flex-col">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">My List</h1>
          <p className="text-gray-600">Create and manage your personal bots</p>
        </div>
        <div className="flex items-center space-x-4">
          <div className="text-sm text-gray-500">
            {totalElements} bots
          </div>
          <button
            onClick={() => setShowCreateModal(true)}
            className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-lg flex items-center space-x-2 transition-colors"
          >
            <Plus className="w-5 h-5" />
            <span>Create Bot</span>
          </button>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-red-600">{error}</p>
          <button
            onClick={() => setError(null)}
            className="mt-2 text-sm text-red-500 hover:text-red-700 underline"
          >
            Dismiss
          </button>
        </div>
      )}

      {/* Search */}
      <div className="mb-6">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
          <input
            type="text"
            placeholder="Search your bots..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
      </div>

      {/* Sort Options */}
      <div className="mb-6 flex flex-wrap gap-2">
        <span className="text-sm text-gray-600 mr-2">Sort by:</span>
        {[
          { field: 'createdAt', label: 'Date Created' },
          { field: 'botName', label: 'Name' },
          { field: 'updatedAt', label: 'Last Updated' },
          { field: 'status', label: 'Status' }
        ].map(({ field, label }) => (
          <button
            key={field}
            onClick={() => handleSort(field)}
            className={`px-3 py-1 rounded-md text-sm transition-colors ${
              sortBy === field
                ? 'bg-blue-500 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            {label} {sortBy === field && (sortDir === 'asc' ? '↑' : '↓')}
          </button>
        ))}
      </div>

      {/* Bots Grid */}
      {loading ? (
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {filteredBots.map((bot) => (
            <div key={bot.botId} className="bg-white rounded-lg shadow-md border border-gray-200 p-6 hover:shadow-lg transition-shadow">
              <div className="flex justify-between items-start mb-4">
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-gray-900 mb-1">{bot.botName}</h3>
                  <p className="text-sm text-gray-600 line-clamp-2">{bot.description || 'No description'}</p>
                </div>
                <div className="flex items-center space-x-1 ml-2">
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(bot.botStatus)}`}>
                    {bot.botStatus === '10001' ? 'DRAFT' : 
                     bot.botStatus === '10002' ? 'PUBLISHED' : 
                     bot.botStatus === '10003' ? 'ARCHIVED' : bot.botStatus}
                  </span>
                  <div className="relative">
                    <button 
                      onClick={(e) => {
                        e.stopPropagation();
                        setOpenMenuId(openMenuId === bot.botId ? null : bot.botId);
                      }}
                      className="p-1 hover:bg-gray-100 rounded-md"
                    >
                      <MoreVertical className="w-4 h-4 text-gray-500" />
                    </button>
                    
                    {openMenuId === bot.botId && (
                      <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg border border-gray-200 py-1 z-50">
                        {bot.botStatus === '10002' && (
                          <>
                            <button
                              onClick={() => handleStatusChange(bot.botId, 'unpublish')}
                              className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center"
                            >
                              <Pause className="h-4 w-4 mr-2" />
                              Unpublish to Draft
                            </button>
                            <button
                              onClick={() => handleStatusChange(bot.botId, 'archive')}
                              className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center"
                            >
                              <Archive className="h-4 w-4 mr-2" />
                              Archive
                            </button>
                          </>
                        )}
                        {bot.botStatus === '10001' && (
                          <>
                            <button
                              onClick={() => handleStatusChange(bot.botId, 'publish')}
                              className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center"
                            >
                              <Play className="h-4 w-4 mr-2" />
                              Publish
                            </button>
                            <button
                              onClick={() => handleStatusChange(bot.botId, 'archive')}
                              className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center"
                            >
                              <Archive className="h-4 w-4 mr-2" />
                              Archive
                            </button>
                          </>
                        )}
                        {bot.botStatus === '10003' && (
                          <button
                            onClick={() => handleStatusChange(bot.botId, 'restore')}
                            className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center"
                          >
                            <Play className="h-4 w-4 mr-2" />
                            Restore to Draft
                          </button>
                        )}
                        <button
                          onClick={() => {
                            setOpenMenuId(null);
                            handleDuplicateBot(bot);
                          }}
                          className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center border-t border-gray-200 mt-1"
                        >
                          <Copy className="h-4 w-4 mr-2" />
                          Duplicate
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className="text-sm text-gray-500 mb-4">
                <p>Created: {new Date(bot.createdAt).toLocaleDateString()}</p>
                <p>Updated: {new Date(bot.updatedAt).toLocaleDateString()}</p>
              </div>

              <div className="flex space-x-2">
                <button
                  onClick={() => handleEditBot(bot)}
                  className="bg-blue-500 hover:bg-blue-600 text-white px-3 py-2 rounded-md text-sm flex items-center justify-center space-x-1 transition-colors"
                  title={bot.botStatus === '10001' ? 'Edit bot' : 'View bot (read-only)'}
                >
                  <Edit className="w-4 h-4" />
                  <span>Edit</span>
                </button>

                <button
                    onClick={() => handleDeleteBot(bot.botId)}
                  className="bg-red-500 hover:bg-red-600 text-white px-3 py-2 rounded-md text-sm flex items-center space-x-1 transition-colors"
                  title="Delete"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {filteredBots.length === 0 && !loading && (
        <div className="text-center py-12">
          <div className="text-gray-400 mb-4">
            <Plus className="w-12 h-12 mx-auto" />
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">No bots found</h3>
          <p className="text-gray-500 mb-4">
            {searchTerm ? 'No bots match your search.' : 'Get started by creating your first bot.'}
          </p>
          {!searchTerm && (
            <button
              onClick={() => setShowCreateModal(true)}
              className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-lg flex items-center space-x-2 mx-auto transition-colors"
            >
              <Plus className="w-5 h-5" />
              <span>Create Bot</span>
            </button>
          )}
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="mt-8 flex justify-center">
          <div className="flex space-x-2">
            <button
              onClick={() => setCurrentPage(Math.max(0, currentPage - 1))}
              disabled={currentPage === 0}
              className="px-3 py-2 border border-gray-300 rounded-md text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
            >
              Previous
            </button>
            
            {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
              const pageNum = Math.max(0, Math.min(totalPages - 1, currentPage - 2 + i));
              return (
                <button
                  key={pageNum}
                  onClick={() => setCurrentPage(pageNum)}
                  className={`px-3 py-2 border rounded-md text-sm ${
                    currentPage === pageNum
                      ? 'bg-blue-500 text-white border-blue-500'
                      : 'border-gray-300 hover:bg-gray-50'
                  }`}
                >
                  {pageNum + 1}
                </button>
              );
            })}
            
            <button
              onClick={() => setCurrentPage(Math.min(totalPages - 1, currentPage + 1))}
              disabled={currentPage === totalPages - 1}
              className="px-3 py-2 border border-gray-300 rounded-md text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
            >
              Next
            </button>
          </div>
        </div>
      )}

      {/* Create Bot Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-96">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Create New Bot</h3>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Bot Name
              </label>
              <input
                type="text"
                value={newBotName}
                onChange={(e) => setNewBotName(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Enter bot name"
                autoFocus
              />
            </div>
            <div className="flex space-x-3">
              <button
                onClick={handleCreateBot}
                disabled={!newBotName.trim() || creating}
                className="flex-1 bg-blue-500 hover:bg-blue-600 disabled:bg-gray-300 text-white px-4 py-2 rounded-md transition-colors flex items-center justify-center"
              >
                {creating ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Creating...
                  </>
                ) : (
                  'Create'
                )}
              </button>
              <button
                onClick={() => {
                  setShowCreateModal(false);
                  setNewBotName('');
                  setError(null);
                }}
                disabled={creating}
                className="flex-1 bg-gray-300 hover:bg-gray-400 disabled:bg-gray-200 text-gray-700 px-4 py-2 rounded-md transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Duplicate Bot Modal */}
      {showDuplicateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-96">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Duplicate Bot</h3>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Bot Name
              </label>
              <input
                type="text"
                value={duplicateBotName}
                onChange={(e) => setDuplicateBotName(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Enter bot name"
                autoFocus
              />
            </div>
            <div className="flex space-x-3">
              <button
                onClick={handleConfirmDuplicate}
                disabled={!duplicateBotName.trim() || duplicating}
                className="flex-1 bg-blue-500 hover:bg-blue-600 disabled:bg-gray-300 text-white px-4 py-2 rounded-md transition-colors flex items-center justify-center"
              >
                {duplicating ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Duplicating...
                  </>
                ) : (
                  'Duplicate'
                )}
              </button>
              <button
                onClick={() => {
                  setShowDuplicateModal(false);
                  setDuplicateBotName('');
                  setSelectedBotForDuplicate(null);
                  setError(null);
                }}
                disabled={duplicating}
                className="flex-1 bg-gray-300 hover:bg-gray-400 disabled:bg-gray-200 text-gray-700 px-4 py-2 rounded-md transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Unpublish Confirmation Modal */}
      {showUnpublishConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Unpublish Bot
            </h3>
            <p className="text-gray-600 mb-6">
              This bot has {activeSessionCount} active user interaction(s). Unpublishing will end all active sessions. Do you want to continue?
            </p>
            <div className="flex space-x-3 justify-end">
              <button
                onClick={handleCancelUnpublish}
                className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmUnpublish}
                className="px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg transition-colors"
              >
                Continue
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MyList;
